// src/components/patients/PatientCard.jsx
import { useMemo, useState } from "react";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import { usePatients } from "../../context/PatientsContext";
import { formatNamePart, buildFullName } from "../../utils/textUtils";

export default function PatientCard({
  patient,
  currentUser = null,
  showRecallFlag = true,
  onClose,
  onDelete: _onDelete,
  onAppointments,
  onPlanTratamente,
  onDetails,
  onFinancial,
  appointmentsList,
  showJournal = true,
}) {
  const {
    addJournalEntry,
    updateJournalEntry,
    deleteJournalEntry,
  } = usePatients() || {};
  const [newMessage, setNewMessage] = useState("");
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editingEntryText, setEditingEntryText] = useState("");

  const appointmentsForPatient = appointmentsList ?? patient.appointments ?? [];
  const hasRecallFlag =
    showRecallFlag &&
    appointmentsForPatient.some(
      (a) => a.recall === "6m" || a.recall === "1y"
    );

  const displayLastName = formatNamePart(patient.lastName);
  const displayFirstName = formatNamePart(patient.firstName);
  const displayFullName = buildFullName(displayLastName, displayFirstName);
  const displayCity = formatNamePart(patient.city || "");
  const isMinor = !!patient.isMinor;
  const representative = patient.legalRepresentative || {};
  const representativeLastName = formatNamePart(representative.lastName || "");
  const representativeFirstName = formatNamePart(representative.firstName || "");
  const representativeCity = formatNamePart(representative.city || "");
  const representativeRelation = representative.relation || "";
  const hasRepresentativeInfo = [
    representativeLastName,
    representativeFirstName,
    representativeCity,
    representativeRelation,
  ].some((value) => Boolean(value && value.trim()));

  const userRole = currentUser?.role || null;
  const userEmail = currentUser?.email?.trim().toLowerCase() || "";
  const canAddJournalEntry = ["admin", "doctor", "assistant"].includes(userRole);
  const canUseJournal =
    showJournal && typeof addJournalEntry === "function" && canAddJournalEntry;

  const resolveEntryType = (entry) => {
    const explicitType = (entry?.type || "").toString().trim().toLowerCase();
    if (explicitType === "audit") return "audit";
    if (explicitType === "note") return "note";
    const text = (entry?.text || "").toString().toLowerCase();
    if (text.includes("radiografie") && /(?:ștear|stear|șters|sters)/u.test(text)) {
      return "hidden";
    }
    return "note";
  };

  const visibleJournalEntries = useMemo(() => {
    const entries = Array.isArray(patient.journal) ? patient.journal : [];
    const scopedEntries = entries.filter((entry) => resolveEntryType(entry) === "note");
    if (userRole === "admin") return scopedEntries;
    if (["doctor", "assistant"].includes(userRole) && userEmail) {
      return scopedEntries.filter((entry) => {
        const createdBy = entry.createdBy || entry.authorEmail || "";
        return createdBy.trim().toLowerCase() === userEmail;
      });
    }
    return scopedEntries;
  }, [patient.journal, userRole, userEmail]);

  const auditEntries = useMemo(() => {
    const entries = Array.isArray(patient.journal) ? patient.journal : [];
    const scopedEntries = entries.filter((entry) => resolveEntryType(entry) === "audit");
    if (userRole === "admin") return scopedEntries;
    if (["doctor", "assistant"].includes(userRole) && userEmail) {
      return scopedEntries.filter((entry) => {
        const createdBy = entry.createdBy || entry.authorEmail || "";
        return createdBy.trim().toLowerCase() === userEmail;
      });
    }
    return scopedEntries;
  }, [patient.journal, userRole, userEmail]);

  const recallLabel = useMemo(() => {
    const recall = (appointmentsForPatient || []).find(
      (a) => a.recall === "6m" || a.recall === "1y"
    );
    if (!recall) return "Programare cu recall";
    const date = recall.date ? new Date(recall.date) : null;
    const readable =
      date && !Number.isNaN(date)
        ? date.toLocaleDateString("ro-RO", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })
        : "n/a";
    const label =
      recall.recall === "1y"
        ? "Recall 1 an"
        : recall.recall === "6m"
        ? "Recall 6 luni"
        : "Recall";
    return `${label}: ${readable}`;
  }, [appointmentsForPatient]);

  // 🔹 Informații despre medic și autor
  const medicCurant =
    (patient.medicName && patient.medicName.trim()) ||
    (patient.meta?.doctorName && patient.meta.doctorName.trim()) ||
    patient.medicEmail ||
    "-";
  const handleAddMessage = async () => {
    if (!canUseJournal) return;
    if (!newMessage.trim()) return;
    try {
      await addJournalEntry(patient.id, newMessage.trim(), "note");
      setNewMessage("");
    } catch (err) {
      alert("Eroare la salvarea notei: " + err.message);
    }
  };

  const handleEditEntry = async (entry) => {
    if (!entry?.id || typeof updateJournalEntry !== "function") return;
    const normalized = (editingEntryText || "").trim();
    if (!normalized) {
      alert("Mesajul nu poate fi gol.");
      return;
    }
    try {
      await updateJournalEntry(patient.id, entry.id, normalized);
      setEditingEntryId(null);
      setEditingEntryText("");
    } catch (err) {
      alert("Eroare la editare: " + err.message);
    }
  };

  const startInlineEdit = (entry) => {
    if (!entry?.id) return;
    setEditingEntryId(entry.id);
    setEditingEntryText(entry.text || "");
  };

  const cancelInlineEdit = () => {
    setEditingEntryId(null);
    setEditingEntryText("");
  };

  const handleDeleteEntry = async (entry) => {
    if (!entry?.id || typeof deleteJournalEntry !== "function") return;
    const confirmed = window.confirm("Ștergi acest mesaj?");
    if (!confirmed) return;
    try {
      await deleteJournalEntry(patient.id, entry.id);
    } catch (err) {
      alert("Eroare la ștergere: " + err.message);
    }
  };

  const containerClasses = useMemo(
    () =>
      `border rounded-xl shadow-sm relative grid gap-6 bg-white/90 ${
        canUseJournal ? "md:grid-cols-[1.2fr_1fr]" : ""
      }`,
    [canUseJournal]
  );

  return (
    <section className="bg-gradient-to-br from-white to-blue-50/30 rounded-2xl shadow-lg p-4 sm:p-5 mb-6 relative z-10">
      {/* 🔹 Câmpuri noi */}
            <p className="mb-3">
              <strong>Medicul curant:</strong>{" "}
              {medicCurant && medicCurant !== "-"
                ? <span className="text-blue-700 font-medium">{medicCurant}</span>
                : "-"}
            </p>

            
      <div className={`${containerClasses} p-4 sm:p-6`}>
        {/* 🔹 Stânga: date pacient */}
        <div>
          {onClose && (
            <Button
              variant="secondary"
              onClick={onClose}
              className="absolute top-2 right-2 !px-2 !py-1"
            >
              ✕
            </Button>
          )}

          <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
            {displayFullName}
            {isMinor && (
              <span className="text-xs font-semibold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
                Minor
              </span>
            )}
            {hasRecallFlag && (
              <span
                className="text-red-500 text-xl"
                title={recallLabel}
                aria-label={recallLabel}
              >
                🏳️
              </span>
            )}
          </h3>

          <div className="space-y-1 text-sm text-gray-600 mb-4">
            <p>
              <strong>Cod pacient:</strong> {patient.code || "-"}
            </p>
            <p>
              <strong>CNP:</strong> {patient.cnp || "-"}
            </p>
            <p>
              <strong>Telefon:</strong> {patient.phone || "-"}
            </p>
            <p>
              <strong>Oraș:</strong> {displayCity || "-"}
            </p>

            

            {(isMinor || hasRepresentativeInfo) && (
              <div className="pt-2 space-y-1">
                <p className="text-xs font-semibold text-gray-600 uppercase">
                  Reprezentant legal
                </p>
                <p>
                  <strong>Nume:</strong> {representativeLastName || "-"}
                </p>
                <p>
                  <strong>Prenume:</strong> {representativeFirstName || "-"}
                </p>
                <p>
                  <strong>Calitate:</strong> {representativeRelation || "-"}
                </p>
                <p>
                  <strong>Oraș:</strong> {representativeCity || "-"}
                </p>
              </div>
            )}

            {patient.notes && (
              <p>
                <strong>Obs:</strong> {patient.notes}
              </p>
            )}
          </div>

          {/* 🔹 Butoane acțiuni */}
          <div className="flex flex-wrap gap-3">
            <div className="flex gap-2">
              {onDetails && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onDetails && onDetails()}
                >
                  📋 Date pacient
                </Button>
              )}
              {onPlanTratamente && (
                <Button variant="success" onClick={onPlanTratamente} size="sm">
                  🦷 Plan tratamente
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {onFinancial && (
                <Button variant="primary" onClick={onFinancial} size="sm">
                  💰 Financiar
                </Button>
              )}
              {onAppointments && (
                <Button variant="primary" onClick={onAppointments} size="sm">
                  📅 Programări
                </Button>
              )}
              {canUseJournal && (
                <Button variant="warning" onClick={() => setShowAuditModal(true)} size="sm">
                  🧾 Audit
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* 🔹 Jurnal (dreapta) */}
        {canUseJournal && (
          <div className="flex flex-col">
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto pr-1">
              {visibleJournalEntries.length === 0 ? (
                <p className="text-gray-500 text-sm">Niciun mesaj adăugat.</p>
              ) : (
                visibleJournalEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="bg-gray-100/80 rounded-lg p-2 text-sm shadow-sm relative"
                  >
                    <p className="text-xs text-gray-500 mb-1">
                      {new Date(entry.createdAt || entry.date).toLocaleString("ro-RO")}
                    </p>
                    {editingEntryId === entry.id ? (
                      <div className="space-y-2">
                        <textarea
                          rows={2}
                          value={editingEntryText}
                          onChange={(e) => setEditingEntryText(e.target.value)}
                          className="w-full border rounded-lg p-2 text-sm bg-white"
                        />
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleEditEntry(entry)}
                            title="Salvează mesaj"
                          >
                            💾
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={cancelInlineEdit}
                            title="Anulează editarea"
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p>{entry.text}</p>
                        <div className="mt-2 flex justify-end gap-1">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => startInlineEdit(entry)}
                            title="Editează mesaj"
                          >
                            ✏️
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteEntry(entry)}
                            title="Șterge mesaj"
                          >
                            🗑️
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="flex gap-2 mt-auto">
              <textarea
                rows={1}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Scrie un mesaj..."
                className="flex-1 border rounded-lg p-2 text-sm"
              />
              <Button variant="success" size="sm" onClick={handleAddMessage}>
                💾 Salvare
              </Button>
            </div>
          </div>
        )}
      </div>

      {showAuditModal && (
        <Modal
          title={`Audit – ${displayFullName || "Pacient"}`}
          onClose={() => setShowAuditModal(false)}
          size="lg"
        >
          <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-1">
            {auditEntries.length === 0 ? (
              <p className="text-gray-500 text-sm">Nicio înregistrare de audit.</p>
            ) : (
              auditEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="bg-gray-100/80 rounded-lg p-2 text-sm shadow-sm relative"
                >
                  <p className="text-xs text-gray-500 mb-1">
                    {new Date(entry.createdAt || entry.date).toLocaleString("ro-RO")}
                  </p>
                  <p>{entry.text}</p>
                </div>
              ))
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}
